import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './LeadsTab.css';
import { apiFetch } from '../lib/api';
import { useProfile } from '../context/UserProfileContext';
import { DIGITALME_ALIGNMENT, TASK_TYPES, TASK_PRIORITIES } from '../lib/leadSignals';

const parseStructuredTasks = (nextActionsText) => {
  if (!nextActionsText) return [];
  const lines = nextActionsText.split('\n').filter(l => l.startsWith('TASK||'));
  return lines.slice(0, 5).map(line => {
    const parts = line.split('||');
    return {
      name:     parts[1] || '',
      taskType: parts[2] || 'Follow-up',
      priority: parts[3] || 'Do Today'
    };
  });
};

// --- TaskRow -- handles creation only, reports back via onCreated ---
const TaskRow = ({ task, leadTitle, accountId, storageKey, createdId, onCreated, checked, onCheck }) => {
  const [name,     setName]     = useState(task.name);
  const [taskType, setTaskType] = useState(task.taskType);
  const [priority, setPriority] = useState(task.priority);
  const [dueDate,  setDueDate]  = useState('');
  const [saving,   setSaving]   = useState(false);

  const handleCreate = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const response = await apiFetch('/api/tasks/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          accountId, name, taskType, priority,
          dueDate: dueDate || null,
          status:  'Approved',
          notes:   `Lead: ${leadTitle}`
        })
      });
      const data = await response.json();
      if (data.success) {
        try { localStorage.setItem(storageKey, data.id); } catch {}
        onCreated(data.id);
      } else {
        alert('Failed to create task: ' + (data.error || 'Unknown error'));
      }
    } catch (err) {
      alert('Network error creating task');
    } finally {
      setSaving(false);
    }
  };

  // -- Processed state --
  if (createdId) {
    return (
      <div className={`task-row task-row-done ${checked ? 'task-row-selected' : ''}`}>
        <input
          type="checkbox"
          className="task-row-checkbox"
          checked={checked}
          onChange={e => onCheck(e.target.checked)}
        />
        <span className="task-row-check">✅</span>
        <span className="task-row-name-done">{name}</span>
        <span className="task-row-created-label">Added to Tasks</span>
      </div>
    );
  }

  // -- Pending state --
  return (
    <div className="task-row">
      <input
        className="task-row-name"
        value={name}
        onChange={e => setName(e.target.value)}
        placeholder="Task name"
      />
      <select className="task-row-select" value={taskType} onChange={e => setTaskType(e.target.value)}>
        {TASK_TYPES.map(t => <option key={t}>{t}</option>)}
      </select>
      <select className="task-row-select task-row-priority" value={priority} onChange={e => setPriority(e.target.value)}>
        {TASK_PRIORITIES.map(p => <option key={p}>{p}</option>)}
      </select>
      <input
        type="date"
        className="task-row-date"
        value={dueDate}
        onChange={e => setDueDate(e.target.value)}
      />
      <button className="task-row-btn" onClick={handleCreate} disabled={saving || !name.trim()}>
        {saving ? '...' : '+ Task'}
      </button>
    </div>
  );
};

// --- Lead Detail Modal ---
const LeadDetailModal = ({ lead, isArchived, onClose, onConvert, onArchive, onUnarchive, onSwitchToOpportunities }) => {
  // See LeadsPage.jsx — same hard-coded tenant name, same fix.
  const { companyName } = useProfile();
  const [activeSection, setActiveSection] = useState('overview');
  const [confirmConvert, setConfirmConvert] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);

  const signals = lead.aiSignals || [];

  const structuredTasks = useMemo(
    () => parseStructuredTasks(lead.nextActions),
    [lead.nextActions]
  );
  const hasStructuredTasks = structuredTasks.length > 0;

  // -- Per-task created IDs (loaded from localStorage) --
  const [createdIds, setCreatedIds] = useState(() => {
    return structuredTasks.map((_, idx) => {
      try { return localStorage.getItem(`task-created-${lead.id}-${idx}`) || null; }
      catch { return null; }
    });
  });

  // -- Checkbox selection (only for created tasks) --
  const [selected, setSelected] = useState(() => structuredTasks.map(() => false));
  const [deleting, setDeleting] = useState(false);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);

  const handleTaskCreated = (idx, taskId) => {
    setCreatedIds(prev => {
      const next = [...prev];
      next[idx] = taskId;
      return next;
    });
  };

  const toggleSelect = (idx, checked) => {
    setSelected(prev => {
      const next = [...prev];
      next[idx] = checked;
      return next;
    });
  };

  const selectedCount = selected.filter((s, i) => s && createdIds[i]).length;

  const handleBulkDelete = async () => {
    const toDelete = structuredTasks
      .map((_, i) => ({ i, taskId: createdIds[i] }))
      .filter(({ i, taskId }) => selected[i] && taskId);

    if (!toDelete.length) return;
    if (!confirmBulkDelete) { setConfirmBulkDelete(true); return; }
    setConfirmBulkDelete(false);

    setDeleting(true);
    try {
      await Promise.all(toDelete.map(({ i, taskId }) =>
        apiFetch('/api/tasks/delete', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ taskId })
        }).then(r => r.json()).then(data => {
          if (data.success) {
            try { localStorage.removeItem(`task-created-${lead.id}-${i}`); } catch {}
            handleTaskCreated(i, null);
          }
        })
      ));
      setSelected(structuredTasks.map(() => false));
    } catch (err) {
      alert('Error deleting tasks');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="lead-modal-overlay" onClick={onClose}>
      <div className="lead-modal" onClick={e => e.stopPropagation()}>

        <div className="lead-modal-header">
          <div className="lead-modal-title-row">
            <div className="lead-modal-badges">
              <span className={`lead-priority lead-priority-${(lead.priority || 'medium').toLowerCase()}`}>
                {lead.priority}
              </span>
              <span className="lead-status-badge">{lead.status}</span>
              {lead.dealSize && <span className="lead-deal-badge">{lead.dealSize}</span>}
            </div>
            <button className="lead-modal-close" onClick={onClose}>✕</button>
          </div>
          <h2 className="lead-modal-title">{lead.title}</h2>

          {signals.length > 0 && (
            <div className="lead-modal-signals">
              {signals.map((sig, i) => (
                <span key={i} className="lead-signal-badge-lg">
                  {DIGITALME_ALIGNMENT[sig]?.icon || '🔔'} {sig}
                </span>
              ))}
            </div>
          )}

          <div className="lead-modal-nav">
            {['overview', 'signals', 'alignment', 'tasks'].map(s => (
              <button
                key={s}
                className={`lead-modal-nav-btn ${activeSection === s ? 'active' : ''}`}
                onClick={() => setActiveSection(s)}
              >
                {s === 'overview'   && '📋 Overview'}
                {s === 'signals'    && `🔔 Signals (${signals.length})`}
                {s === 'alignment'  && `🎯 ${companyName} Fit`}
                {s === 'tasks'      && `✅ Tasks${hasStructuredTasks ? ` (${structuredTasks.length})` : ''}`}
              </button>
            ))}
          </div>
        </div>

        <div className="lead-modal-body">

          {activeSection === 'overview' && (
            <div className="lead-modal-section">
              {lead.hypothesis && (
                <div className="lead-detail-block">
                  <h4>Opportunity Hypothesis</h4>
                  <p>{lead.hypothesis}</p>
                </div>
              )}
              {lead.companyOverview && (
                <div className="lead-detail-block">
                  <h4>Opportunity Overview</h4>
                  <pre className="lead-detail-pre">{lead.companyOverview}</pre>
                </div>
              )}
              {lead.executiveIntelligence && (
                <div className="lead-detail-block">
                  <h4>Target Executive</h4>
                  <pre className="lead-detail-pre">{lead.executiveIntelligence}</pre>
                </div>
              )}
              {lead.technologyLandscape && (
                <div className="lead-detail-block">
                  <h4>Affected Technology</h4>
                  <pre className="lead-detail-pre">{lead.technologyLandscape}</pre>
                </div>
              )}
              {!lead.hypothesis && !lead.companyOverview && !lead.executiveIntelligence && (
                <p className="lead-empty-note">Run research on this account to populate detailed intelligence.</p>
              )}
            </div>
          )}

          {activeSection === 'signals' && (
            <div className="lead-modal-section">
              {signals.length === 0 ? (
                <p className="lead-empty-note">No AI signals tagged for this lead.</p>
              ) : (
                signals.map((sig, i) => {
                  const info = DIGITALME_ALIGNMENT[sig];
                  return (
                    <div key={i} className="signal-card">
                      <div className="signal-card-header">
                        <span className="signal-icon">{info?.icon || '🔔'}</span>
                        <strong>{info?.label || sig}</strong>
                      </div>
                      {info && <p className="signal-card-body">{info.pitch(companyName)}</p>}
                    </div>
                  );
                })
              )}
            </div>
          )}

          {activeSection === 'alignment' && (
            <div className="lead-modal-section">
              {signals.length === 0 ? (
                <p className="lead-empty-note">Tag AI signals to this lead to see DigitalMe service alignment.</p>
              ) : (
                <>
                  <div className="alignment-intro">
                    <p>Based on the signals detected, here's how DigitalMe's capabilities align to this opportunity:</p>
                  </div>
                  {signals.map((sig, i) => {
                    const info = DIGITALME_ALIGNMENT[sig];
                    if (!info) return null;
                    return (
                      <div key={i} className="alignment-card">
                        <div className="alignment-card-header">
                          <span>{info.icon}</span>
                          <strong>{info.label}</strong>
                        </div>
                        <div className="alignment-services">
                          {info.services.map((svc, j) => (
                            <span key={j} className="alignment-service-tag">{svc}</span>
                          ))}
                        </div>
                        <p className="alignment-pitch">{info.pitch(companyName)}</p>
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          )}

          {activeSection === 'tasks' && (
            <div className="lead-modal-section">
              {hasStructuredTasks ? (
                <div className="task-rows-container">
                  <p className="task-rows-intro">
                    Edit any field, then click <strong>+ Task</strong> to create it on your dashboard.
                  </p>
                  <div className="task-rows-header">
                    <span className="task-col-check"></span>
                    <span className="task-col-name">Task</span>
                    <span className="task-col-type">Type</span>
                    <span className="task-col-priority">Priority</span>
                    <span className="task-col-date">Due Date</span>
                    <span className="task-col-action"></span>
                  </div>
                  {structuredTasks.map((task, idx) => (
                    <TaskRow
                      key={`${lead.id}-task-${idx}`}
                      task={task}
                      leadTitle={lead.title}
                      accountId={lead.accountId}
                      storageKey={`task-created-${lead.id}-${idx}`}
                      createdId={createdIds[idx]}
                      onCreated={(taskId) => handleTaskCreated(idx, taskId)}
                      checked={selected[idx] && !!createdIds[idx]}
                      onCheck={(val) => toggleSelect(idx, val)}
                    />
                  ))}

                  {/* Bulk delete bar -- only shows when created tasks are checked */}
                  {selectedCount > 0 && (
                    <div className="task-bulk-delete-bar">
                      <span className="task-bulk-count">{selectedCount} task{selectedCount > 1 ? 's' : ''} selected</span>
                      {confirmBulkDelete ? (
                        <>
                          <button className="task-bulk-delete-btn task-bulk-confirm" onClick={handleBulkDelete} disabled={deleting}>Confirm delete?</button>
                          <button className="task-bulk-cancel-btn" onClick={() => setConfirmBulkDelete(false)}>Cancel</button>
                        </>
                      ) : (
                        <button className="task-bulk-delete-btn" onClick={handleBulkDelete} disabled={deleting}>
                          {deleting ? 'Deleting...' : `🗑 Delete Selected`}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="task-legacy">
                  {lead.nextActions && (
                    <div className="lead-detail-block">
                      <h4>Suggested Next Actions</h4>
                      <pre className="lead-detail-pre">{lead.nextActions}</pre>
                    </div>
                  )}
                  <p className="task-note">Re-create this lead from research to get one-click task creation.</p>
                </div>
              )}
              <p className="task-note">Tasks appear on your Dashboard and Tasks tab.</p>
            </div>
          )}
        </div>

        <div className="lead-modal-footer">
          {isArchived ? (
            <button className="btn-unarchive-modal" onClick={() => { onUnarchive(lead); onClose(); }}>
              ↩ Restore Lead
            </button>
          ) : lead.convertedOpportunity ? (
            <button
              className="btn-view-opportunity"
              onClick={() => { onSwitchToOpportunities && onSwitchToOpportunities(); onClose(); }}
            >
              ✓ View Opportunity →
            </button>
          ) : (
            <>
              {confirmConvert ? (
                <>
                  <button className="btn-convert-modal btn-confirm" onClick={() => { onConvert(lead); onClose(); }}>Confirm convert?</button>
                  <button className="btn-archive-modal" onClick={() => setConfirmConvert(false)}>Cancel</button>
                </>
              ) : confirmArchive ? (
                <>
                  <button className="btn-archive-modal btn-confirm" onClick={() => { onArchive(lead); onClose(); }}>Confirm archive?</button>
                  <button className="btn-convert-modal" onClick={() => setConfirmArchive(false)}>Cancel</button>
                </>
              ) : (
                <>
                  <button className="btn-convert-modal" onClick={() => setConfirmConvert(true)}>Convert to Opportunity</button>
                  <button className="btn-archive-modal" onClick={() => setConfirmArchive(true)}>Archive</button>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// --- Main LeadsTab ---
const LeadsTab = ({ accountId, onSwitchTab }) => {
  const [leads, setLeads]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);

  const fetchLeads = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    try {
      const response = await apiFetch(`/api/leads/by-account?accountId=${accountId}`);
      if (!response.ok) throw new Error('Failed to fetch leads');
      const data = await response.json();
      setLeads(data.leads || []);
    } catch (error) {
      console.error('Error fetching leads:', error);
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const convertToOpportunity = async (lead) => {
    try {
      const response = await apiFetch('/api/leads/convert-to-opportunity', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ leadId: lead.id })
      });
      if (!response.ok) throw new Error('Failed to convert lead');
      await fetchLeads();
    } catch (error) {
      console.error('Error converting lead:', error);
      alert('❌ Failed to convert lead to opportunity');
    }
  };

  const archiveLead = async (lead) => {
    try {
      const response = await apiFetch('/api/leads/archive', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ leadId: lead.id })
      });
      if (!response.ok) throw new Error('Failed to archive lead');
      await fetchLeads();
    } catch (error) {
      console.error('Error archiving lead:', error);
      alert('❌ Failed to archive lead');
    }
  };

  const unarchiveLead = async (lead) => {
    try {
      const response = await apiFetch('/api/leads/unarchive', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ leadId: lead.id })
      });
      if (!response.ok) throw new Error('Failed to unarchive lead');
      await fetchLeads();
    } catch (error) {
      console.error('Error unarchiving lead:', error);
    }
  };

  const displayLeads  = showArchived
    ? leads.filter(l => l.status === 'Archived')
    : leads.filter(l => l.status !== 'Archived');
  const activeCount   = leads.filter(l => l.status !== 'Archived').length;
  const archivedCount = leads.filter(l => l.status === 'Archived').length;

  if (loading) return <div className="leads-loading">Loading leads...</div>;

  return (
    <div className="leads-tab">
      <div className="leads-header">
        <div className="leads-header-left">
          <h3>Leads {activeCount > 0 && <span className="leads-count">{activeCount}</span>}</h3>
        </div>
        <div className="leads-header-right">
          {archivedCount > 0 && (
            <button className="btn-toggle-archive" onClick={() => setShowArchived(!showArchived)}>
              <span className="toggle-icon">{showArchived ? '◀' : '▶'}</span>
              {showArchived ? `Active (${activeCount})` : `Archived (${archivedCount})`}
            </button>
          )}
        </div>
      </div>

      {displayLeads.length === 0 ? (
        <div className="leads-empty">
          {showArchived ? (
            <p>No archived leads.</p>
          ) : (
            <>
              <p>No active leads for this account.</p>
              <p className="leads-empty-hint">Run research on this company to identify opportunities.</p>
            </>
          )}
        </div>
      ) : (
        <div className="leads-grid">
          {displayLeads.map(lead => (
            <div
              key={lead.id}
              className={`lead-card lead-card-clickable ${showArchived ? 'lead-archived' : `lead-${(lead.status || '').toLowerCase().replace(/\s+/g, '-')}`}`}
              onClick={() => setSelectedLead(lead)}
            >
              <div className="lead-header">
                <h4>{lead.title}</h4>
                <span className={`lead-priority lead-priority-${(lead.priority || 'medium').toLowerCase()}`}>
                  {lead.priority}
                </span>
              </div>
              <div className="lead-meta">
                <span className="lead-status">{lead.status}</span>
                {lead.dealSize && <span className="lead-deal-size">{lead.dealSize}</span>}
              </div>
              {lead.aiSignals && lead.aiSignals.length > 0 && (
                <div className="lead-signals">
                  {lead.aiSignals.map((signal, idx) => (
                    <span key={idx} className="lead-signal-badge">
                      {DIGITALME_ALIGNMENT[signal]?.icon || '🔔'} {signal}
                    </span>
                  ))}
                </div>
              )}
              {lead.hypothesis && <p className="lead-hypothesis">{lead.hypothesis}</p>}
              <div className="lead-card-footer">
                <span className="lead-click-hint">Click to expand →</span>
                {lead.convertedOpportunity && <span className="lead-converted-badge">✓ Converted</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedLead && (
        <LeadDetailModal
          lead={{ ...selectedLead, accountId }}
          isArchived={showArchived}
          onClose={() => setSelectedLead(null)}
          onConvert={convertToOpportunity}
          onArchive={archiveLead}
          onUnarchive={unarchiveLead}
          onSwitchToOpportunities={() => onSwitchTab && onSwitchTab('opportunities')}
        />
      )}
    </div>
  );
};

export default LeadsTab;
